import { useRef, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { Locate } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { notify } from '../../services/notify';
import { LocationPicker, formatAddress, type LocationPickerHandle, type LocationPickerValue } from './LocationPicker';
import { LocaleSearchInput } from './LocaleSearchInput';
import { useUpdateProfileAddress } from '../../hooks/useProfile';
import { useResolvePlace } from '../../hooks/useMaps';
import { isSufficientLocation, useLocationStore } from '../../store/useLocationStore';
import { useProfileStore } from '../../store/useProfileStore';
import type { Address } from '../../types/address';

interface ProfileLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * 'profile' (default): standalone profile-address editor.
   * 'publish': invoked mid-publish for a chef with no profile address — shows
   * the publish prompt copy, offers the dish's own location (see `dishLocation`)
   * as a one-tap option, and defers post-save side effects to `onSaved`.
   */
  mode?: 'profile' | 'publish';
  /**
   * Publish mode only. When provided, the user is first asked whether to reuse
   * this location (Yes/No) before the picker is shown. "Yes" seeds the picker
   * with it (still editable); "No" starts from an empty picker.
   */
  dishLocation?: LocationPickerValue | null;
  /**
   * Publish mode only. Called after the profile address is successfully saved.
   * When set, the modal skips its default side effects (updating the browse
   * location + success toast) so the caller can resume its own flow.
   */
  onSaved?: () => void;
}

export const ProfileLocationModal: React.FC<ProfileLocationModalProps> = ({
  isOpen,
  onClose,
  mode = 'profile',
  dishLocation = null,
  onSaved,
}) => {
  const { t } = useTranslation('location');
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const setLocation = useLocationStore((s) => s.setLocation);
  const updateProfileAddress = useUpdateProfileAddress();
  const resolvePlaceMutation = useResolvePlace();

  const [value, setValue] = useState<LocationPickerValue | null>(null);
  const locationPickerRef = useRef<LocationPickerHandle>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [aptResolved, setAptResolved] = useState(true);
  const [pinResolved, setPinResolved] = useState(true);
  // Profile mode only: a searched city or typed ZIP, the minimum an orderer needs.
  const [query, setQuery] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  // Publish mode: whether the user has answered the "use this dish's location?"
  // prompt yet. Until they do, the picker is hidden behind the Yes/No choice.
  const [dishChoiceMade, setDishChoiceMade] = useState(false);

  const isPublish = mode === 'publish';
  const showDishGate = isPublish && !!dishLocation && !dishChoiceMade;

  const profileAddress =
    activeProfile?.metadata?.addresses?.find((a) => a.type === 'profile') ?? null;

  // Seed/reset on open-close transitions during render (not in an effect) so the
  // seeded value is in place BEFORE LocationPicker mounts. If we seeded in an
  // effect, the child's own init effect runs first (child effects fire before
  // parent's), sees a null value, and runs buildProfileLocation — which rejects
  // an imprecise (no-street) saved address and falls back to the device's current
  // location, the exact bug we're fixing. Pre-seeding makes the child's init
  // short-circuit on a non-null value. Publish mode has no profile address (it
  // uses the dish-location gate), so it seeds null there.
  const [prevOpen, setPrevOpen] = useState(false);
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    setValue(
      isOpen && !isPublish && profileAddress
        ? { address: profileAddress, displayText: formatAddress(profileAddress) }
        : null,
    );
    setGeoError(null);
    setAptResolved(true);
    setPinResolved(true);
    setDishChoiceMade(false);
    setQuery('');
    setSearchError(null);
  }

  const handleUseDishLocation = () => {
    setValue(dishLocation ?? null);
    setDishChoiceMade(true);
  };

  const handleEnterManual = () => {
    setValue(null);
    setDishChoiceMade(true);
  };

  // Step back from the picker to the dish-location choice. Without this the
  // choice is one-way: `dishChoiceMade` only ever resets on an open->close
  // transition, so the Modal's X (and Cancel) on the picker screen tore down
  // the whole gate and abandoned the publish with no address saved.
  const handleBackToDishChoice = () => {
    setValue(null);
    setDishChoiceMade(false);
    setGeoError(null);
    setSearchError(null);
    setQuery('');
    setAptResolved(true);
    setPinResolved(true);
  };

  // True while the picker is showing on top of a dish-location choice the user
  // can return to. Dismissing there means "back", not "abandon" — the caller's
  // onClose (which, in publish mode, cancels the publish) is reserved for the
  // choice screen itself.
  const canStepBack = isPublish && !!dishLocation && dishChoiceMade;
  const handleDismiss = canStepBack ? handleBackToDishChoice : onClose;

  // Minimum-info path (orderers): resolve a searched city or typed ZIP to its
  // centroid and keep only postal/country (+ state/city for display).
  // Deliberately no street1, no lat/lon, no pinConfirmed — that's enough for
  // tax, and keeps the saved address recognizably incomplete so publish
  // (isFullProfileAddress) still prompts a lister to finish a real street.
  //
  // For a city, `useResolvePlace` backfills the postal code from the city
  // centroid, so the saved value is city-accurate rather than street-accurate.
  // Same bargain the ZIP path already made, one notch coarser.
  const handleSearchSubmit = (text: string) => {
    setSearchError(null);
    const trimmed = text.trim();
    if (!trimmed) {
      setSearchError(t('errors.searchRequired'));
      return;
    }
    resolvePlaceMutation.mutate(trimmed, {
      onSuccess: (address) => {
        if (!isSufficientLocation(address)) {
          setSearchError(t('errors.searchUnresolved'));
          return;
        }
        const minimal: Address = {
          type: 'profile',
          postalCode: address.postalCode,
          country: address.country,
          stateProvince: address.stateProvince,
          city: address.city,
        };
        setValue({ address: minimal, displayText: formatAddress(minimal) });
      },
      onError: () => setSearchError(t('errors.searchUnresolved')),
    });
  };

  const handleUseCurrentLocation = async () => {
    setGeoError(null);
    setGeoLoading(true);
    try {
      await locationPickerRef.current?.useCurrentLocation();
    } catch (err) {
      setGeoError(err instanceof Error ? err.message : t('profileModal.errorGeneric'));
    } finally {
      setGeoLoading(false);
    }
  };

  const handleSave = async () => {
    if (!activeProfile || !value) return;
    const addr = value.address;
    try {
      await updateProfileAddress.mutateAsync({
        profileId: activeProfile.id,
        address: {
          type: 'profile',
          street1: addr.street1,
          street2: addr.street2,
          city: addr.city,
          stateProvince: addr.stateProvince,
          postalCode: addr.postalCode,
          country: addr.country,
          lat: addr.lat,
          lon: addr.lon,
          noUnit: addr.noUnit,
          pinConfirmed: addr.pinConfirmed,
        },
      });
      if (onSaved) {
        // Publish flow owns post-save messaging/navigation AND closing the
        // modal; don't touch the browse location, fire a toast the resumed
        // publish would race, or call onClose — the caller treats onClose as
        // "the user abandoned the gate" and would cancel the publish.
        onSaved();
      } else {
        setLocation(addr);
        notify.success(t('profileModal.toast.updated'));
        onClose();
      }
    } catch {
      notify.error(t('profileModal.toast.updateFailed'));
    }
  };

  const saving = updateProfileAddress.isPending;
  // Stripe tax needs country + postal on the saved address. The picker can emit
  // a pin-confirmed location without them (e.g. a non-geocodable pin), which the
  // apt/pin gates don't catch — so require both explicitly before allowing save.
  const addressComplete = !!value?.address.country && !!value?.address.postalCode;
  // Profile mode is the orderer minimum: country + postal is enough, so don't
  // hold the save behind the apt/pin gates (those exist to make a *full* street
  // address precise). Publish mode keeps the strict gate — a lister needs a
  // real, complete address.
  const canSave = !!value && addressComplete && (!isPublish || (aptResolved && pinResolved));

  return (
    <Modal isOpen={isOpen} onClose={handleDismiss} size="sm" zIndexClass="z-[70]" className="flex flex-col gap-3">
      <h2 className="text-lg font-bold text-foreground">
        {isPublish ? t('profileModal.publish.title') : t('profileModal.title')}
      </h2>
      {isPublish && (
        <p className="text-sm text-muted-foreground">{t('profileModal.publish.prompt')}</p>
      )}
      {showDishGate ? (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleUseDishLocation}
            className="rounded-xl bg-brand-primary py-3 text-base font-semibold text-white shadow-sm transition active:opacity-80"
          >
            {t('profileModal.publish.useDishCta')}
          </button>
          <button
            type="button"
            onClick={handleEnterManual}
            className="rounded-xl border border-border py-3 text-base font-semibold text-foreground transition active:opacity-80"
          >
            {t('profileModal.publish.enterManualCta')}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={geoLoading}
              className="inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
              style={{ color: 'var(--ion-color-primary)' }}
            >
              <Locate size={16} />
              {geoLoading ? t('profileModal.gettingLocation') : t('profileModal.useCurrentCta')}
            </button>
            {geoError && (
              <p
                className="text-xs"
                style={{ color: 'var(--ion-color-danger)' }}
              >
                {geoError}
              </p>
            )}
          </div>
          {isOpen && (
            <LocationPicker
              ref={locationPickerRef}
              value={value}
              onChange={setValue}
              profileAddress={profileAddress}
              onAptResolutionChange={setAptResolved}
              onPinResolutionChange={setPinResolved}
              // Profile mode accepts a ZIP-only address — hide the map / pin
              // step unless the user enters a real street. Publish needs the
              // full street address, so it keeps the map + pin controls.
              hideMapAndPinForZip={!isPublish}
            />
          )}
          {/* Minimum-info path: orderers only need country + postal, so let them
              set just a city or ZIP without a full street address. Hidden in
              publish mode, where a complete address is required. */}
          {!isPublish && (
            <>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wide text-muted-foreground">{t('divider.or')}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <LocaleSearchInput
                inputId="profile-location-search"
                size="sm"
                value={query}
                onChange={setQuery}
                onSubmit={handleSearchSubmit}
                submitting={resolvePlaceMutation.isPending}
                error={searchError}
                label={t('search.label')}
                placeholder={t('search.placeholder')}
                ctaLabel={t('selector.updateCta')}
              />
            </>
          )}
        </>
      )}
      <div className="mt-1 flex justify-end gap-2">
        <button
          type="button"
          onClick={handleDismiss}
          disabled={saving}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          {canStepBack ? t('common:actions.back') : t('common:actions.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave || saving}
          className="inline-flex items-center justify-center rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <IonSpinner name="crescent" className="h-5 w-5" /> : t('common:actions.save')}
        </button>
      </div>
    </Modal>
  );
};
